// Lightweight session tracking for platform analytics.
// Anonymous, hashed session IDs stored in sessionStorage. Rolls every 30 min idle.

const SESSION_KEY = 'cs-analytics-session';
const IDLE_MS = 30 * 60 * 1000;

interface StoredSession {
  id: string;
  startedAt: number;
  lastSeenAt: number;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

function writeStored(s: StoredSession) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / private mode */
  }
}

export function getSessionId(): string {
  const now = Date.now();
  const existing = readStored();
  if (existing && now - existing.lastSeenAt < IDLE_MS) {
    existing.lastSeenAt = now;
    writeStored(existing);
    return existing.id;
  }
  const fresh: StoredSession = { id: randomId(), startedAt: now, lastSeenAt: now };
  writeStored(fresh);
  return fresh.id;
}

export function isNewSession(): boolean {
  const s = readStored();
  return !s || Date.now() - s.startedAt < 2000;
}

export function getSessionMeta() {
  const ua = navigator.userAgent;
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
  const isTablet = /Tablet|iPad/i.test(ua);
  const deviceType = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

  let browser = 'Other';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';

  let os = 'Other';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return { deviceType, browser, os };
}

export function parseUtm(search: string) {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
}
